import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiClock, FiSearch, FiDownload, FiPrinter, FiPackage, FiArrowUpRight, FiArrowDownLeft } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { formatDate as fmtDate } from '../../utils/formatters';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import useReportMemory from '../../hooks/useReportMemory';
import PageLayout from '../../components/PageLayout';
import ItemSelect from '../../components/ItemSelect';
import { getStockFlowByCriteria, ACTIVE_STOCK_STATUSES } from '../../utils/stockFlowHelper';
import { formatStockFlowQtyCell } from '../../utils/unitConversion';
import { navigateToTransaction } from '../../utils/navigationHelper';
import { addPrintBrand } from '../../utils/printBranding';

// Styled Components
const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover {
      background: ${colors.primary.dark};
    }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover {
      background: ${colors.neutral[200]};
    }
  `}
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const ItemHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const ItemTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ItemCode = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 12px;
  padding: 4px 10px;
  background: ${colors.primary.main}15;
  color: ${colors.primary.main};
  border-radius: ${colors.borderRadius.sm};
  font-weight: 500;
`;

const ItemSubtitle = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const SummaryCard = styled.div`
  background: white;
  padding: 14px;
  border-radius: ${colors.borderRadius.md};
  border: 1px solid ${colors.neutral[200]};
`;

const SummaryLabel = styled.div`
  font-size: 11px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  text-transform: uppercase;
`;

const SummaryValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.type === 'in' ? colors.success.main : props.type === 'out' ? colors.error.main : colors.text.primary};
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }

  ${props => props.isOpening && `
    background: ${colors.primary.main}08;
    font-weight: 500;
  `}
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 13px;
  color: ${props => props.type === 'in' ? colors.success.main : props.type === 'out' ? colors.error.main : colors.text.primary};
`;

const TransactionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    switch (props.type) {
      case 'PI': case 'GRN': case 'AS': return colors.success.main + '15';
      case 'SI': case 'PS': return colors.primary.main + '15';
      case 'DC': case 'ST': case 'SR': return colors.info.main + '15';
      case 'PR': case 'RS': return colors.warning.main + '15';
      case 'OB': return colors.neutral[200];
      default: return colors.neutral[200];
    }
  }};
  color: ${props => {
    switch (props.type) {
      case 'PI': case 'GRN': case 'AS': return colors.success.main;
      case 'SI': case 'PS': return colors.primary.main;
      case 'DC': case 'ST': case 'SR': return colors.info.main;
      case 'PR': case 'RS': return colors.warning.main;
      case 'OB': return colors.text.secondary;
      default: return colors.text.secondary;
    }
  }};
`;

const DirectionIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: ${colors.borderRadius.sm};
  background: ${props => props.direction === 'in' ? colors.success.main + '15' : colors.error.main + '15'};
  color: ${props => props.direction === 'in' ? colors.success.main : colors.error.main};
  margin-right: 8px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

const formatNumber = (num) => {
  if (!num && num !== 0) return '-';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

const formatCurrencyValue = (num, symbol = 'Rs.') => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!num && num !== 0) return '-';
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  }).format(num);
  return `${symbol}${formatted}`;
};

const formatDate = (v) => fmtDate(v) || '-';

const getTransactionTypeLabel = (type) => {
  const labels = {
    'PI': 'Purchase Invoice',
    'SI': 'Sale Invoice',
    'GRN': 'Goods Receipt',
    'DC': 'Delivery Challan',
    'ST': 'Stock Transfer',
    'AS': 'Stock Add',
    'RS': 'Stock Reduce',
    'PR': 'Purchase Return',
    'SR': 'Sale Return',
    'OB': 'Opening Balance',
    'PS': 'POS Sale',
    'PO': 'Purchase Order',
    'SO': 'Sale Order',
    'QT': 'Quotation'
  };
  return labels[type] || type;
};

const InventoryLedger = () => {
  const { activeCompany, activeBranch } = useAuth();
  const [savedMem, saveMemory] = useReportMemory('inventory-ledger');
  const currencySymbol = activeCompany?.currencySymbol || 'Rs.';
  const formatCurrency = (num) => formatCurrencyValue(num, currencySymbol);
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedItem, setSelectedItem] = useState(() => savedMem?.selectedItem ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [transactionType, setTransactionType] = useState(() => savedMem?.transactionType ?? 'all');
  const [selectedLocation, setSelectedLocation] = useState(() => savedMem?.selectedLocation ?? 'all');
  const [selectedDirection, setSelectedDirection] = useState(() => savedMem?.selectedDirection ?? 'all');
  const [historyEntries, setHistoryEntries] = useState(() => savedMem?.historyEntries ?? []);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);

  const [summary, setSummary] = useState(() => savedMem?.summary ?? {
    openingBalance: 0,
    totalIn: 0,
    totalOut: 0,
    closingBalance: 0,
    transactionCount: 0
  });

  // Load items and locations for dropdowns
  const loadMasterData = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      const [itemsSnap, locsSnap] = await Promise.all([
        getDocs(query(collection(db, `companies/${activeCompany.id}/inventoryItems`), orderBy('itemCode', 'asc'))),
        getDocs(query(collection(db, `companies/${activeCompany.id}/inventoryLocations`), orderBy('locationCode', 'asc')))
      ]);

      setItems(itemsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(i => i.isDeleted !== true));
      setLocations(locsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading master data:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadMasterData();
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [loadMasterData, savedMem]);

  // Handle incoming navigation state for pre-selected item
  const processedStateRef = useRef(false);
  const autoLoadRef = useRef(false);
  useEffect(() => {
    if (processedStateRef.current) return;
    if (savedMem) return; // restored from memory — keep restored filters/results, don't re-process nav state or auto-load
    const state = location.state;
    if (!state) return;

    if (state.itemId && items.length > 0) {
      const item = items.find(i => i.id === state.itemId);
      if (item) {
        processedStateRef.current = true;
        autoLoadRef.current = true;
        setSelectedItem(state.itemId);
      }
    }
  }, [location.state, items, savedMem]);

  const loadLedger = async () => {
    if (!activeCompany?.id || !selectedItem) {
      alert('Please select an item');
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      // Use stockFlowHelper to avoid composite index issues
      const rawEntries = await getStockFlowByCriteria(activeCompany.id, {
        itemId: selectedItem,
        status: ACTIVE_STOCK_STATUSES, // shared list (incl. 'Invoiced') so invoiced-DC out-movements stay in the ledger
        branchId: (activeBranch && activeBranch.id !== 'all') ? activeBranch.id : undefined
      });

      // Sort client-side by docDate ascending
      const allTransactions = rawEntries.sort((a, b) =>
        (a.docDate || '').localeCompare(b.docDate || '')
      );

      // Filter and process transactions
      const filteredTransactions = [];
      let runningBalance = 0;
      let runningCostValue = 0;
      let totalIn = 0;
      let totalOut = 0;
      let openingBalance = 0;

      allTransactions.forEach(transaction => {
        const transDate = transaction.docDate;

        // Location filter
        if (selectedLocation !== 'all' && transaction.locationId !== selectedLocation) return;

        // Before date range → add to opening balance
        if (dateFrom && transDate < dateFrom) {
          if (transaction.direction === 'in') {
            openingBalance += transaction.quantity || 0;
            runningBalance += transaction.quantity || 0;
            runningCostValue += transaction.totalCost || 0;
          } else {
            openingBalance -= transaction.quantity || 0;
            runningBalance -= transaction.quantity || 0;
            runningCostValue -= transaction.totalCost || 0;
          }
          return;
        }

        // After date range → skip
        if (dateTo && transDate > dateTo) return;

        // Filter by transaction type
        if (transactionType !== 'all' && transaction.tranTypeCode !== transactionType) return;

        // Filter by direction
        if (selectedDirection !== 'all' && transaction.direction !== selectedDirection) return;

        const qty = transaction.quantity || 0;
        const cost = transaction.totalCost || 0;
        const direction = transaction.direction || 'in';

        if (direction === 'in') {
          runningBalance += qty;
          runningCostValue += cost;
          totalIn += qty;
        } else {
          runningBalance -= qty;
          runningCostValue -= cost;
          totalOut += qty;
        }

        const unitCost = qty > 0 ? (cost / qty) : 0;

        filteredTransactions.push({
          ...transaction,
          balance: runningBalance,
          unitCost,
          totalCostValue: cost
        });
      });

      const summaryObj = {
        openingBalance,
        totalIn,
        totalOut,
        closingBalance: runningBalance,
        transactionCount: filteredTransactions.length
      };

      setSummary(summaryObj);

      setHistoryEntries(filteredTransactions);

      saveMemory({
        selectedItem,
        dateFrom,
        dateTo,
        transactionType,
        selectedLocation,
        selectedDirection,
        historyEntries: filteredTransactions,
        summary: summaryObj,
        hasSearched: true
      });

    } catch (error) {
      console.error('Error loading inventory ledger:', error);
      setHistoryEntries([]);
      setSummary({ openingBalance: 0, totalIn: 0, totalOut: 0, closingBalance: 0, transactionCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate report when navigated from another page with a pre-selected item
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoLoadRef.current && selectedItem && dateFrom && dateTo) {
      autoLoadRef.current = false;
      loadLedger();
    }
  }, [selectedItem]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrint = () => {
    const selectedItemData = items.find(item => item.id === selectedItem);
    if (!selectedItemData || historyEntries.length === 0) { alert('No data to print'); return; }

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Inventory Ledger - ${selectedItemData.itemName}</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:11px;margin-bottom:18px}
        .summary{display:flex;gap:24px;margin-bottom:18px;padding:12px 15px;background:#f9f9f9;border-radius:8px;flex-wrap:wrap}
        .summary-item{text-align:center;min-width:100px}
        .summary-label{font-size:10px;color:#666;margin-bottom:3px}
        .summary-value{font-size:15px;font-weight:600}
        .in{color:#1565C0} .out{color:#C62828}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase}
        td{padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px}
        .amount{text-align:right;font-family:'Consolas',monospace}
        .opening-row,.closing-row{background:#f5f5f5;font-weight:600}
        .tran-badge{font-size:9px;padding:2px 5px;border-radius:3px;background:#eee}
        @media print{body{margin:15px}@page{size:landscape}}
      </style></head><body>
      <h1>Inventory Ledger</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Item: ${selectedItemData.itemCode} - ${selectedItemData.itemName} |
        Unit: ${selectedItemData.unitOfMeasure || 'Pcs'} |
        Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)}
      </div>
      <div class="summary">
        <div class="summary-item"><div class="summary-label">Opening Balance</div><div class="summary-value">${formatNumber(summary.openingBalance)}</div></div>
        <div class="summary-item"><div class="summary-label">Total In</div><div class="summary-value in">${formatNumber(summary.totalIn)}</div></div>
        <div class="summary-item"><div class="summary-label">Total Out</div><div class="summary-value out">${formatNumber(summary.totalOut)}</div></div>
        <div class="summary-item"><div class="summary-label">Closing Balance</div><div class="summary-value">${formatNumber(summary.closingBalance)}</div></div>
        <div class="summary-item"><div class="summary-label">Transactions</div><div class="summary-value">${summary.transactionCount}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Date</th><th>Doc #</th><th>Type</th><th>Description</th><th>Location</th>
          <th class="amount">Qty In</th><th class="amount">Qty Out</th><th class="amount">Balance</th>
          <th class="amount">Unit Cost</th><th class="amount">Total Cost</th>
        </tr></thead>
        <tbody>
          <tr class="opening-row">
            <td>${formatDate(dateFrom)}</td><td>-</td><td>-</td>
            <td>Opening Balance</td><td>-</td>
            <td class="amount">-</td><td class="amount">-</td>
            <td class="amount">${formatNumber(summary.openingBalance)}</td>
            <td class="amount">-</td><td class="amount">-</td>
          </tr>
          ${historyEntries.map(e => `<tr>
            <td>${formatDate(e.docDate)}</td>
            <td>${e.docNumber || '-'}</td>
            <td><span class="tran-badge">${getTransactionTypeLabel(e.tranTypeCode)}</span></td>
            <td>${e.description || '-'}</td>
            <td>${e.locationName || '-'}</td>
            <td class="amount in">${e.direction === 'in' ? formatStockFlowQtyCell(e) : '-'}</td>
            <td class="amount out">${e.direction === 'out' ? formatStockFlowQtyCell(e) : '-'}</td>
            <td class="amount">${formatNumber(e.runningBalance)}</td>
            <td class="amount">${formatCurrency(e.unitCostValue || 0)}</td>
            <td class="amount">${formatCurrency(e.totalCostValue || 0)}</td>
          </tr>`).join('')}
          <tr class="closing-row">
            <td colspan="5" style="text-align:right">Closing Balance</td>
            <td class="amount in">${formatNumber(summary.totalIn)}</td>
            <td class="amount out">${formatNumber(summary.totalOut)}</td>
            <td class="amount">${formatNumber(summary.closingBalance)}</td>
            <td class="amount">-</td><td class="amount">-</td>
          </tr>
        </tbody>
      </table>
      <div style="font-size:10px;color:#999;margin-top:10px">Printed on ${new Date().toLocaleString()}</div>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  const handleExport = () => {
    const selectedItemData = items.find(item => item.id === selectedItem);
    if (!selectedItemData) return;

    let csv = `Inventory Ledger - ${selectedItemData.itemName}\n`;
    csv += `Code: ${selectedItemData.itemCode}\n`;
    csv += `Period: ${formatDate(dateFrom)} to ${formatDate(dateTo)}\n\n`;
    csv += `Opening Balance:,${summary.openingBalance}\n`;
    csv += `Total In:,${summary.totalIn}\n`;
    csv += `Total Out:,${summary.totalOut}\n`;
    csv += `Closing Balance:,${summary.closingBalance}\n\n`;
    csv += `Date,Document No,Type,Description,Location,Qty In,Qty Out,Balance,Unit Cost,Total Cost\n`;

    historyEntries.forEach(entry => {
      csv += `${formatDate(entry.docDate)},`;
      csv += `${entry.docNumber || ''},`;
      csv += `${getTransactionTypeLabel(entry.tranTypeCode)},`;
      csv += `"${(entry.description || '').replace(/"/g, '""')}",`;
      csv += `"${(entry.locationName || '').replace(/"/g, '""')}",`;
      csv += `${entry.direction === 'in' ? (entry.quantity || 0) : ''},`;
      csv += `${entry.direction === 'out' ? (entry.quantity || 0) : ''},`;
      csv += `${entry.balance || 0},`;
      csv += `${(entry.unitCost || 0).toFixed(2)},`;
      csv += `${(entry.totalCostValue || 0).toFixed(2)}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Inventory_Ledger_${selectedItemData.itemCode}_${dateFrom}_${dateTo}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const selectedItemData = items.find(item => item.id === selectedItem);

  return (
    <PageLayout pageTitle="Inventory Ledger" breadcrumb="Reports / Inventory Reports / Inventory Ledger">
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Item *</Label>
            <ItemSelect
              items={items}
              selectedItemId={selectedItem}
              onChange={setSelectedItem}
              placeholder="Select Item"
            />
          </FilterGroup>

          <FilterGroup>
            <Label>From Date</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>To Date</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Location</Label>
            <Select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <option value="all">All Locations</option>
              {locations.map(loc => (
                <option key={loc.id} value={loc.id}>{loc.locationName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Transaction Type</Label>
            <Select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
            >
              <option value="all">All Types</option>
              <option value="PI">Purchase Invoice</option>
              <option value="SI">Sale Invoice</option>
              <option value="GRN">Goods Receipt</option>
              <option value="DC">Delivery Challan</option>
              <option value="ST">Stock Transfer</option>
              <option value="AS">Stock Add</option>
              <option value="RS">Stock Reduce</option>
              <option value="PR">Purchase Return</option>
              <option value="SR">Sale Return</option>
              <option value="OB">Opening Balance</option>
              <option value="PS">POS Sale</option>
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Direction</Label>
            <Select
              value={selectedDirection}
              onChange={(e) => setSelectedDirection(e.target.value)}
            >
              <option value="all">All</option>
              <option value="in">In Only</option>
              <option value="out">Out Only</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={historyEntries.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={historyEntries.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button primary onClick={loadLedger} disabled={loading}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      <ContentCard>
        {hasSearched && selectedItemData && (
          <ItemHeader>
            <ItemTitle>
              <FiPackage />
              {selectedItemData.itemName}
              <ItemCode>{selectedItemData.itemCode}</ItemCode>
            </ItemTitle>
            <ItemSubtitle>
              {formatDate(dateFrom)} to {formatDate(dateTo)} | Unit: {selectedItemData.unitOfMeasure || 'N/A'}
            </ItemSubtitle>
            <SummaryGrid>
              <SummaryCard>
                <SummaryLabel>Opening Balance</SummaryLabel>
                <SummaryValue>{formatNumber(summary.openingBalance)}</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Total In</SummaryLabel>
                <SummaryValue type="in">+{formatNumber(summary.totalIn)}</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Total Out</SummaryLabel>
                <SummaryValue type="out">-{formatNumber(summary.totalOut)}</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Closing Balance</SummaryLabel>
                <SummaryValue>{formatNumber(summary.closingBalance)}</SummaryValue>
              </SummaryCard>
              <SummaryCard>
                <SummaryLabel>Total Transactions</SummaryLabel>
                <SummaryValue>{summary.transactionCount}</SummaryValue>
              </SummaryCard>
            </SummaryGrid>
          </ItemHeader>
        )}

        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading inventory ledger...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiClock /></EmptyIcon>
              <EmptyText>Select an item and date range to view the ledger</EmptyText>
            </EmptyState>
          ) : historyEntries.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiClock /></EmptyIcon>
              <EmptyText>No transactions found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Document No</Th>
                  <Th>Type</Th>
                  <Th>Description</Th>
                  <Th>Location</Th>
                  <Th align="right">Qty In</Th>
                  <Th align="right">Qty Out</Th>
                  <Th align="right">Balance</Th>
                  <Th align="right">Unit Cost</Th>
                  <Th align="right">Total Cost</Th>
                </Tr>
              </Thead>
              <Tbody>
                {/* Opening Balance Row */}
                <Tr isOpening>
                  <Td>{formatDate(dateFrom)}</Td>
                  <Td>-</Td>
                  <Td><TransactionBadge type="OB">Opening</TransactionBadge></Td>
                  <Td>Opening Balance</Td>
                  <Td>-</Td>
                  <AmountCell align="right">-</AmountCell>
                  <AmountCell align="right">-</AmountCell>
                  <AmountCell align="right">{formatNumber(summary.openingBalance)}</AmountCell>
                  <AmountCell align="right">-</AmountCell>
                  <AmountCell align="right">-</AmountCell>
                </Tr>

                {historyEntries.map((entry, index) => (
                  <Tr key={entry.id || index}>
                    <Td>{formatDate(entry.docDate)}</Td>
                    <Td>
                      {entry.transactionId && entry.tranTypeCode ? (
                        <span
                          style={{ color: colors.primary.main, cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => navigateToTransaction(navigate, entry.tranTypeCode, entry.transactionId)}
                          title="Open source document"
                        >
                          {entry.docNumber || '-'}
                        </span>
                      ) : (entry.docNumber || '-')}
                    </Td>
                    <Td>
                      <TransactionBadge type={entry.tranTypeCode}>
                        <DirectionIcon direction={entry.direction}>
                          {entry.direction === 'in' ? <FiArrowDownLeft size={12} /> : <FiArrowUpRight size={12} />}
                        </DirectionIcon>
                        {getTransactionTypeLabel(entry.tranTypeCode)}
                      </TransactionBadge>
                    </Td>
                    <Td>{entry.description || '-'}</Td>
                    <Td>{entry.locationName || '-'}</Td>
                    <AmountCell align="right" type={entry.direction === 'in' ? 'in' : undefined}>
                      {entry.direction === 'in' ? formatStockFlowQtyCell(entry) : '-'}
                    </AmountCell>
                    <AmountCell align="right" type={entry.direction === 'out' ? 'out' : undefined}>
                      {entry.direction === 'out' ? formatStockFlowQtyCell(entry) : '-'}
                    </AmountCell>
                    <AmountCell align="right">{formatNumber(entry.balance)}</AmountCell>
                    <AmountCell align="right">{formatCurrency(entry.unitCost)}</AmountCell>
                    <AmountCell align="right">{formatCurrency(entry.totalCostValue)}</AmountCell>
                  </Tr>
                ))}

                {/* Closing Balance Row */}
                <Tr style={{ background: colors.neutral[100], fontWeight: 600 }}>
                  <Td colSpan={5} style={{ textAlign: 'right' }}>Closing Balance</Td>
                  <AmountCell align="right" type="in" style={{ fontWeight: 600 }}>
                    {formatNumber(summary.totalIn)}
                  </AmountCell>
                  <AmountCell align="right" type="out" style={{ fontWeight: 600 }}>
                    {formatNumber(summary.totalOut)}
                  </AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 600 }}>
                    {formatNumber(summary.closingBalance)}
                  </AmountCell>
                  <AmountCell align="right" colSpan={2}>-</AmountCell>
                </Tr>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>
    </PageLayout>
  );
};

export default InventoryLedger;
